// Shared editors for manufacturer-facing data entry (parts, packaging,
// manufacturing, transport). Extracted from the retired supplier-authenticated
// ManufacturerPortal page so the public token-gated portal renders the same
// tables against the same reference data. Everything is prop-driven — no auth
// context, callers own the state.

import React, { useMemo } from 'react';
import AddressAutocomplete from '../AddressAutocomplete/AddressAutocomplete';

export const ENERGY_SOURCES = [
  { value: 'grid', label: 'Grid electricity' },
  { value: 'renewable', label: 'Renewable (solar/wind)' },
  { value: 'mixed', label: 'Mixed' },
];

export function blankPart() {
  return { name: '', weight_grams: '', material_category: '', material_subcategory: '', material_quality: '', process_name: '' };
}
export function blankPackaging() {
  return { type: '', weight: '' };
}
export function blankTransport() {
  return {
    mode: '', distance_km: '', origin_country: '', destination_country: '',
    origin_lat: null, origin_lng: null, destination_lat: null, destination_lng: null,
  };
}

export function unwrap(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return payload?.data || payload || [];
}

// ---------- Section components ----------

export function PartsSection({ parts, materialCategories, subcatCache, ensureSubcategories, onAdd, onChange, onRemove, hint }) {
  const catNames = useMemo(
    () => (materialCategories || []).map((c) => (typeof c === 'string' ? c : c.category || c.name)).filter(Boolean),
    [materialCategories]
  );
  return (
    <SubSection
      title="Parts"
      hint={hint ?? 'Each physical component of the product.'}
      onAdd={onAdd}
      addLabel="+ Add part"
      columns={['Weight (g)', 'Material category', 'Subcategory', 'Quality / spec', 'Process']}
      colTemplate="110px 1fr 1fr 1fr 1fr 40px"
      empty="No parts yet — add the components that make up this product."
      rows={(parts || []).map((part, i) => ({
        cells: [
          <InlineInput
            type="number"
            value={part.weight_grams ?? ''}
            placeholder="g"
            onChange={(v) => onChange(i, { weight_grams: v === '' ? '' : Number(v) })}
          />,
          <SelectField
            value={part.material_category || ''}
            options={catNames}
            placeholder="Select material"
            onChange={(v) => {
              onChange(i, { material_category: v, material_subcategory: '' });
              if (v && ensureSubcategories) ensureSubcategories(v);
            }}
          />,
          <SelectField
            value={part.material_subcategory || ''}
            options={((subcatCache || {})[part.material_category] || [])
              .map((s) => (typeof s === 'string' ? s : s.subcategory || s.name))
              .filter(Boolean)}
            placeholder={part.material_category ? 'Select sub' : '—'}
            disabled={!part.material_category}
            onChange={(v) => onChange(i, { material_subcategory: v })}
          />,
          <InlineInput
            value={part.material_quality || ''}
            placeholder="e.g. FSC, food-grade"
            onChange={(v) => onChange(i, { material_quality: v })}
          />,
          <InlineInput
            value={part.process_name || ''}
            placeholder="e.g. Injection moulding"
            onChange={(v) => onChange(i, { process_name: v })}
          />,
        ],
        onRemove: () => onRemove(i),
      }))}
    />
  );
}

export function PackagingSection({ packaging, packagingTypes, onAdd, onChange, onRemove, hint }) {
  const opts = [
    { value: 'Loose Packed', label: 'Loose Packed' },
    ...(packagingTypes || []).map((t) => ({ value: t.value || t.id || t.label, label: t.label || t.name || t.value })),
  ];
  return (
    <SubSection
      title="Packaging"
      hint={hint ?? 'Boxes, polybags, labels, tape.'}
      onAdd={onAdd}
      addLabel="+ Add packaging"
      columns={['Type', 'Weight (g)']}
      colTemplate="1fr 130px 40px"
      empty="No packaging recorded yet."
      rows={(packaging || []).map((pk, i) => ({
        cells: [
          <SelectField
            value={pk.type || ''}
            options={opts.map((o) => o.label)}
            valueFor={(label) => opts.find((o) => o.label === label)?.value || label}
            displayFor={(value) => opts.find((o) => o.value === value)?.label || value}
            placeholder="Type"
            onChange={(v) => onChange(i, { type: v, ...(v === 'Loose Packed' ? { weight: '' } : {}) })}
          />,
          pk.type === 'Loose Packed' ? null : (
            <InlineInput
              type="number"
              value={pk.weight ?? ''}
              placeholder="g"
              onChange={(v) => onChange(i, { weight: v === '' ? '' : Number(v) })}
            />
          ),
        ],
        onRemove: () => onRemove(i),
      }))}
    />
  );
}

export function ManufacturingSection({ value, countries, onChange, hint }) {
  const countryNames = (countries || []).map((c) => (typeof c === 'string' ? c : c.country_name || c.name)).filter(Boolean);
  const v = value || {};
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      <div className="d-flex align-items-center justify-content-between" style={{ padding: '10px 14px', background: '#fcfcfd', borderBottom: '1px solid #f3f4f6' }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Manufacturing</div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>{hint ?? 'Where and how the product is made.'}</div>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 560 }}>
          <div
            className="d-grid"
            style={{
              gridTemplateColumns: '1fr 1fr 1fr 130px',
              background: '#fafbfc',
              borderBottom: '1px solid #f3f4f6',
              fontSize: 11,
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            <div style={{ padding: '8px 12px' }}>Country</div>
            <div style={{ padding: '8px 12px' }}>Energy source</div>
            <div style={{ padding: '8px 12px' }}>Energy use (kWh)</div>
            <div style={{ padding: '8px 12px' }}>Renewable %</div>
          </div>
          <div className="d-grid align-items-center" style={{ gridTemplateColumns: '1fr 1fr 1fr 130px' }}>
            <div style={{ padding: '6px 12px' }}>
              <SelectField
                value={v.assembly_location || ''}
                options={countryNames}
                placeholder="Country"
                onChange={(val) => onChange({ assembly_location: val })}
              />
            </div>
            <div style={{ padding: '6px 12px' }}>
              <SelectField
                value={v.energy_source || 'grid'}
                options={ENERGY_SOURCES.map((e) => e.label)}
                valueFor={(label) => ENERGY_SOURCES.find((e) => e.label === label)?.value || label}
                displayFor={(value2) => ENERGY_SOURCES.find((e) => e.value === value2)?.label || value2}
                placeholder="Energy"
                onChange={(val) => onChange({ energy_source: val })}
              />
            </div>
            <div style={{ padding: '6px 12px' }}>
              <InlineInput
                type="number"
                value={v.manufacturing_energy_consumption_kwh ?? ''}
                placeholder="kWh"
                onChange={(val) => onChange({ manufacturing_energy_consumption_kwh: val === '' ? '' : Number(val) })}
              />
            </div>
            <div style={{ padding: '6px 12px' }}>
              <InlineInput
                type="number"
                value={v.renewable_percentage ?? ''}
                placeholder="0–100"
                onChange={(val) => onChange({ renewable_percentage: val === '' ? '' : Number(val) })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Mirrors the distance estimate in Step4AssemblyDistribution.js (LCA wizard) —
// keep the two in sync if the multipliers change there.
const toRad = (deg) => (deg * Math.PI) / 180;
function haversineKm(a, b) {
  if (!a || !b) return 0;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return Math.round(R * c);
}
function getModeMultiplier(mode) {
  if (!mode) return 1.2;
  const m = mode.toLowerCase();
  if (m.includes('air')) return 1.0;
  if (m.includes('sea') || m.includes('ship') || m.includes('ferry') || m.includes('boat')) return 1.2;
  if (m.includes('road') || m.includes('truck') || m.includes('hgv') || m.includes('van')) return 1.3;
  if (m.includes('rail') || m.includes('train')) return 1.15;
  return 1.2;
}

const ADDRESS_INPUT_STYLE = {
  width: '100%',
  border: '1px solid #e5e7eb',
  background: '#fff',
  borderRadius: 6,
  padding: '6px 8px',
  fontSize: 13,
  color: '#111827',
  outline: 'none',
};

export function TransportSection({ legs, transportModes, onAdd, onChange, onRemove, hint }) {
  const modeOpts = (transportModes || []).map((m) => ({ value: m.value || m.id, label: m.label || m.name || m.value }));

  // Recompute distance once both ends have coordinates, same as the wizard.
  const recalcDistance = (leg, updates) => {
    const originLat = 'origin_lat' in updates ? updates.origin_lat : leg.origin_lat;
    const originLng = 'origin_lng' in updates ? updates.origin_lng : leg.origin_lng;
    const destLat = 'destination_lat' in updates ? updates.destination_lat : leg.destination_lat;
    const destLng = 'destination_lng' in updates ? updates.destination_lng : leg.destination_lng;
    if (originLat == null || originLng == null || destLat == null || destLng == null) return null;
    const mode = updates.mode ?? leg.mode;
    const km = haversineKm({ lat: originLat, lon: originLng }, { lat: destLat, lon: destLng });
    return Math.round(km * getModeMultiplier(mode));
  };

  const handleAddressSelect = (i, leg, field, result) => {
    const isOrigin = field === 'origin';
    const updates = isOrigin
      ? { origin_country: result?.address || '', origin_lat: result?.lat ?? null, origin_lng: result?.lng ?? null }
      : { destination_country: result?.address || '', destination_lat: result?.lat ?? null, destination_lng: result?.lng ?? null };
    if (!result?.typed) {
      const distance = recalcDistance(leg, updates);
      if (distance != null) updates.distance_km = distance;
    }
    onChange(i, updates);
  };

  const handleModeChange = (i, leg, mode) => {
    const updates = { mode };
    const distance = recalcDistance(leg, updates);
    if (distance != null) updates.distance_km = distance;
    onChange(i, updates);
  };

  // A grid/table layout clips the address dropdown against the row's
  // horizontal-scroll wrapper (SubSection), forcing a scrollbar to see
  // suggestions. Stacked cards — same shape as Step4AssemblyDistribution.js —
  // give each leg room to breathe and avoid that clipping entirely.
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      <div className="d-flex align-items-center justify-content-between" style={{ padding: '10px 14px', background: '#fcfcfd', borderBottom: '1px solid #f3f4f6' }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Upstream transport</div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>{hint ?? 'How raw parts get to the factory.'}</div>
        </div>
        <button
          onClick={onAdd}
          className="btn btn-sm"
          style={{ background: '#111827', color: '#fff', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}
        >
          + Add transport leg
        </button>
      </div>

      {(!legs || legs.length === 0) ? (
        <div style={{ padding: '12px 14px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No upstream legs yet.</div>
      ) : (
        legs.map((tr, i) => (
          <div key={i} style={{ padding: '14px', borderBottom: i === legs.length - 1 ? 'none' : '1px solid #f3f4f6' }}>
            <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Leg {i + 1}</span>
              <button
                onClick={() => onRemove(i)}
                style={{ background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 16, padding: '2px 6px', borderRadius: 4 }}
                title="Remove"
              >
                ×
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>From</div>
                <AddressAutocomplete
                  value={tr.origin_country || ''}
                  onSelect={(result) => handleAddressSelect(i, tr, 'origin', result)}
                  placeholder="Type a city, port or address..."
                  inputStyle={ADDRESS_INPUT_STYLE}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>To</div>
                <AddressAutocomplete
                  value={tr.destination_country || ''}
                  onSelect={(result) => handleAddressSelect(i, tr, 'destination', result)}
                  placeholder="Type a city, port or address..."
                  inputStyle={ADDRESS_INPUT_STYLE}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Mode</div>
                <SelectField
                  value={tr.mode || ''}
                  options={modeOpts.map((o) => o.label)}
                  valueFor={(label) => modeOpts.find((o) => o.label === label)?.value || label}
                  displayFor={(value) => modeOpts.find((o) => o.value === value)?.label || value}
                  placeholder="Mode"
                  onChange={(v) => handleModeChange(i, tr, v)}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Distance (km)</div>
                <InlineInput
                  type="number"
                  value={tr.distance_km ?? ''}
                  placeholder="km"
                  onChange={(v) => onChange(i, { distance_km: v === '' ? '' : Number(v) })}
                />
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ---------- Primitives ----------

export function InlineInput({ value, onChange, type = 'text', placeholder, style }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        border: '1px solid #e5e7eb',
        background: '#fff',
        borderRadius: 6,
        padding: '6px 8px',
        fontSize: 13,
        color: '#111827',
        outline: 'none',
        ...style,
      }}
      onFocus={(e) => { e.target.style.border = '1px solid #c7d2fe'; }}
      onBlur={(e) => { e.target.style.border = '1px solid #e5e7eb'; }}
    />
  );
}

export function SelectField({ value, options, placeholder, onChange, disabled, valueFor, displayFor }) {
  const display = displayFor ? displayFor(value) : value;
  return (
    <select
      value={display || ''}
      onChange={(e) => {
        const v = e.target.value;
        onChange(valueFor ? valueFor(v) : v);
      }}
      disabled={disabled}
      style={{
        width: '100%',
        border: '1px solid #e5e7eb',
        background: '#fff',
        borderRadius: 6,
        padding: '6px 8px',
        fontSize: 13,
        color: value ? '#111827' : '#9ca3af',
        outline: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onFocus={(e) => { e.target.style.border = '1px solid #c7d2fe'; }}
      onBlur={(e) => { e.target.style.border = '1px solid #e5e7eb'; }}
    >
      <option value="">{placeholder}</option>
      {(options || []).map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

export function SubSection({ title, hint, onAdd, addLabel, columns, colTemplate, rows, empty }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      <div className="d-flex align-items-center justify-content-between" style={{ padding: '10px 14px', background: '#fcfcfd', borderBottom: '1px solid #f3f4f6' }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{title}</div>
          {hint ? <div style={{ fontSize: 12, color: '#9ca3af' }}>{hint}</div> : null}
        </div>
        {onAdd ? (
          <button
            onClick={onAdd}
            className="btn btn-sm"
            style={{ background: '#111827', color: '#fff', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            {addLabel}
          </button>
        ) : null}
      </div>

      {rows.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 640 }}>
            <div
              className="d-grid"
              style={{
                gridTemplateColumns: colTemplate,
                background: '#fafbfc',
                borderBottom: '1px solid #f3f4f6',
                fontSize: 11,
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              {columns.map((c, i) => <div key={i} style={{ padding: '8px 12px' }}>{c}</div>)}
              <div />
            </div>
            {rows.map((row, idx) => (
              <div
                key={idx}
                className="d-grid align-items-center"
                style={{
                  gridTemplateColumns: colTemplate,
                  borderBottom: idx === rows.length - 1 ? 'none' : '1px solid #f9fafb',
                }}
              >
                {row.cells.map((c, i) => <div key={i} style={{ padding: '6px 12px' }}>{c}</div>)}
                <div style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {row.onRemove ? (
                    <button
                      onClick={row.onRemove}
                      style={{ background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 16, padding: '2px 6px', borderRadius: 4 }}
                      title="Remove"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ padding: '12px 14px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>{empty}</div>
      )}
    </div>
  );
}
